import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import html2canvas from 'html2canvas';
import { ToastrService } from 'ngx-toastr';
import { DailyLogConflict } from './daily-log-insert-popup.component';

@Component({
  selector: 'app-daily-log-conflicts-dialog',
  templateUrl: './daily-log-conflicts-dialog.component.html',
  styleUrls: ['./daily-log-conflicts-dialog.component.scss'],
})
export class DailyLogConflictsDialogComponent {
  @ViewChild('captureRoot') captureRoot?: ElementRef<HTMLElement>;

  isCapturing = false;
  private readonly fieldLabels: Record<string, string> = {
    wind_direction_deg: 'Wind Direction (deg)',
    wind_speed_kn: 'Wind Speed (kn)',
    tank_avg_temp_map: 'Tank Avg Temp',
    bc_total_hfo_mt: 'Bunker HFO (MT)',
    bc_total_mgo_mt: 'Bunker MGO (MT)',
    air_temperature_c: 'Air Temperature (°C)',
    seawater_temperature_c: 'Seawater Temperature (°C)',
    heating_duration_hr: 'Heating Duration (hr)',
  };

  constructor(
    private dialogRef: MatDialogRef<DailyLogConflictsDialogComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { conflicts: DailyLogConflict[] },
  ) {}

  get conflicts(): DailyLogConflict[] {
    return this.data?.conflicts || [];
  }

  formatParameter(field: string): string {
    return this.fieldLabels[field] || field.replace(/_/g, ' ');
  }

  formatTank(tank?: string): string {
    if (!tank) {
      return '—';
    }
    return tank.replace(/-/g, ' ');
  }

  formatDate(dateStr: string): string {
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  formatValue(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '—';
    }
    if (typeof value === 'number') {
      return Number.isInteger(value) ? String(value) : value.toFixed(2);
    }
    const num = Number(value);
    if (!Number.isNaN(num) && String(value).trim() !== '') {
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    }
    return String(value);
  }

  close(): void {
    this.dialogRef.close();
  }

  async copyScreenshot(): Promise<void> {
    const element = this.captureRoot?.nativeElement;
    if (!element || this.isCapturing) {
      return;
    }

    this.isCapturing = true;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (_document, clonedElement) => {
          clonedElement.querySelectorAll('.screenshot-exclude').forEach((node) => {
            (node as HTMLElement).style.display = 'none';
          });

          const tableWrap = clonedElement.querySelector(
            '.table-wrap',
          ) as HTMLElement | null;
          if (tableWrap) {
            tableWrap.style.maxHeight = 'none';
            tableWrap.style.overflow = 'visible';
          }

          clonedElement.querySelectorAll('.conflicts-table th').forEach((header) => {
            (header as HTMLElement).style.position = 'static';
          });

          (clonedElement as HTMLElement).style.maxHeight = 'none';
        },
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) {
        throw new Error('Failed to create screenshot image');
      }

      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        this.toastr.success(
          'Screenshot copied. Paste it into your email (Ctrl+V / Cmd+V).',
        );
      } else {
        this.downloadScreenshot(blob);
        this.toastr.info(
          'Screenshot downloaded. Attach the PNG file to your email.',
        );
      }
    } catch (error) {
      console.error('Conflict screenshot failed:', error);
      this.toastr.error(
        'Could not copy screenshot. Your browser may block clipboard access.',
      );
    } finally {
      this.isCapturing = false;
    }
  }

  private downloadScreenshot(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `daily-log-conflicts-${dateStamp}.png`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
